import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export type EntryMode = "fixed" | "minimum_consumption";

export interface EntryType {
  id: string;
  restaurant_id: string;
  name: string;
  amount: number;
  entry_mode: EntryMode;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useEntryTypes() {
  const { activeRestaurant } = useRestaurantContext();
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchEntryTypes = async () => {
    if (!activeRestaurant?.id) return;

    try {
      const { data, error } = await supabase
        .from("entry_types")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("name");

      if (error) throw error;

      // Map the data to ensure proper typing
      const typedData: EntryType[] = (data || []).map(item => ({
        ...item,
        entry_mode: item.entry_mode as EntryMode,
        is_active: item.is_active ?? true
      }));

      setEntryTypes(typedData);
    } catch (error) {
      console.error("Error fetching entry types:", error);
      toast.error("Erro ao carregar tipos de entrada");
    } finally {
      setIsLoading(false);
    }
  };

  const createEntryType = async (data: {
    name: string;
    amount: number;
    entry_mode: EntryMode;
  }) => {
    if (!activeRestaurant?.id) return null;

    try {
      const { data: newEntryType, error } = await supabase
        .from("entry_types")
        .insert({
          restaurant_id: activeRestaurant.id,
          name: data.name,
          amount: data.amount,
          entry_mode: data.entry_mode,
        })
        .select()
        .single();

      if (error) throw error;

      const typed: EntryType = {
        ...newEntryType,
        entry_mode: newEntryType.entry_mode as EntryMode,
        is_active: newEntryType.is_active ?? true
      };

      setEntryTypes((prev) => [...prev, typed]);
      toast.success("Tipo de entrada criado!");
      return typed;
    } catch (error) {
      console.error("Error creating entry type:", error);
      toast.error("Erro ao criar tipo de entrada");
      return null;
    }
  };

  const updateEntryType = async (
    id: string,
    data: Partial<Pick<EntryType, "name" | "amount" | "entry_mode" | "is_active">>
  ) => {
    try {
      const { error } = await supabase
        .from("entry_types")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;

      setEntryTypes((prev) =>
        prev.map((et) => (et.id === id ? { ...et, ...data } : et))
      );
      toast.success("Tipo de entrada atualizado!");
      return true;
    } catch (error) {
      console.error("Error updating entry type:", error);
      toast.error("Erro ao atualizar tipo de entrada");
      return false;
    }
  };

  const deleteEntryType = async (id: string) => {
    try {
      const { error } = await supabase
        .from("entry_types")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setEntryTypes((prev) => prev.filter((et) => et.id !== id));
      toast.success("Tipo de entrada removido!");
      return true;
    } catch (error) {
      console.error("Error deleting entry type:", error);
      toast.error("Erro ao remover tipo de entrada");
      return false;
    }
  };

  const getActiveEntryTypes = () => {
    return entryTypes.filter((et) => et.is_active);
  };

  useEffect(() => {
    fetchEntryTypes();
  }, [activeRestaurant?.id]);

  return {
    entryTypes,
    isLoading,
    createEntryType,
    updateEntryType,
    deleteEntryType,
    getActiveEntryTypes,
    refetch: fetchEntryTypes,
  };
}
